/**
 * @file Freya-micro-frontend-framework
 * @stage1 @current 实现加载功能
 * @stage2 拆包
 * @stage3 子应用管理+框架主体实现
 */
export interface Asset {
  tagName: string
  attributes: {
    [key: string]: any
  }
}

interface AssetsMap {
  bodyTags: Asset[]
  headTags: Asset[]
}

const assert = (assets: AssetsMap) => {
  if (
    Object.prototype.toString.call(assets) !==
    '[object Object]'
  ) {
    throw new Error(
      'Please provide A mapping that includes headTags and bodyTags with the assets.'
    )
  }
  if (!Array.isArray(assets.bodyTags)) {
    throw new Error('bodyTags must be an array.')
  }
  if (!Array.isArray(assets.headTags)) {
    throw new Error('headTags must be an array.')
  }
}

/**
 *
 * A lite framework for micro frontend, it's can be used to load assets in the browser.
 *
 * @dependencies /freya/micro/kyc => assetsMaps with headTags and bodyTags.
 * @usage const loadRunner = new FreyaMicroFrontendFramework({ headTags:[...], bodyTags:[...] })
 * @usage loadRunner.run()
 */
export class FreyaMicroFrontendFramework {
  constructor(assets: AssetsMap) {
    assert(assets)
    this.cache = new Map(
      [...assets.headTags, ...assets.bodyTags].map(
        (tag) => [
          (tag?.attributes?.src
            ? tag.attributes.src
            : tag.attributes.href) || 'about:blank',
          tag,
        ]
      )
    )
  }
  private cache = new Map()
  clean() {
    this.cache.forEach((_, key) => {
      try {
        const el = document.getElementById(key)
        el?.parentNode?.removeChild(el)
      } catch (e) {
        console.log('Not important.')
      }
    })
    this.cache.clear()
  }
  private status = 'unknown'
  private fallback: (() => void)[] = []
  async run(cb?: () => void) {
    if (this.status === 'loaded') {
      return cb && cb()
    }
    if (this.status === 'loading') {
      if (cb) {
        if (this.fallback.indexOf(cb) === -1) {
          this.fallback.push(cb)
        }
      }
      return
    }
    this.status = 'loading'
    cb && this.fallback.push(cb)
    const pre = []
    // for await (const [url, entry] of this.cache) {
    //   await this.load(url, entry)
    // }
    for (const [url, entry] of this.cache) {
      pre.push([url, entry])
    }
    await Promise.all(
      pre.map(([url, entry]) => this.load(url, entry))
    )
    this.status = 'loaded'
    while (this.fallback.length) {
      const callback = this.fallback.shift()
      callback && callback()
    }
  }
  private load(url: string, entry: Asset) {
    return new Promise((resolve) => {
      if (document.getElementById(url)) {
        return resolve('done')
      }
      const el = document.createElement(entry.tagName)
      if (entry.tagName === 'script') {
        el.onload = () => {
          el.onload = el.onerror = null
          resolve('done')
        }
        el.onerror = () => {
          el.onload = el.onerror = null
          resolve('error')
        }
      } else {
        setTimeout(() => resolve('done'), 0)
      }
      Object.keys(entry.attributes).forEach((key) => {
        el.setAttribute('id', url)
        el.setAttribute(
          key,
          entry.attributes[key] as string
        )
      })
      document.body.appendChild(el)
    })
  }
}
